import "./stock.css";
import { useEffect, useState, useMemo } from "react";
import { userRequest } from "../../requestMethods";

export default function Stock() {
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, inStock, outStock, lowStock, negativeMargin

  useEffect(() => {
    const getProducts = async () => {
      try {
        const res = await userRequest.get("/products");
        const products = res.data;
        const flattened = flattenProducts(products);
        setStockData(flattened);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };
    getProducts();
  }, []);

  const flattenProducts = (products) => {
    let rows = [];
    products.forEach((product) => {
      const supplier = Array.isArray(product.mark) ? product.mark.join(", ") : product.mark;
      const baseBuyingPrice = product.buyingPrice || 0;
      const basePrice = product.price || 0;

      if (product.attr && product.attr.length > 0) {
        product.attr.forEach((variant, index) => {
          const margin = basePrice - baseBuyingPrice;
          // Updated formula: (Margin / Selling Price) * 100
          const percentMargin = basePrice > 0 ? (margin / basePrice) * 100 : 0;

          rows.push({
            id: `${product._id}-${index}`,
            supplier: supplier,
            ref: product.ref,
            declinaison: `${product.title} ${variant.size}`,
            price: basePrice,
            margin: margin,
            percentMargin: percentMargin,
            qteDispo: variant.quantity,
            title: product.title, // for search
            buyingPrice: baseBuyingPrice
          });
        });
      } else {
        const margin = basePrice - baseBuyingPrice;
        // Updated formula: (Margin / Selling Price) * 100
        const percentMargin = basePrice > 0 ? (margin / basePrice) * 100 : 0;

        rows.push({
          id: product._id,
          supplier: supplier,
          ref: product.ref,
          declinaison: product.title,
          price: basePrice,
          margin: margin,
          percentMargin: percentMargin,
          qteDispo: product.quantity,
          title: product.title,
          buyingPrice: baseBuyingPrice
        });
      }
    });
    return rows;
  };

  const filteredData = useMemo(() => {
    let data = stockData;

    // 1. Apply Search
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      data = data.filter(
        (row) =>
          row.declinaison.toLowerCase().includes(lowerSearch) ||
          row.ref.toLowerCase().includes(lowerSearch) ||
          (row.supplier && row.supplier.toLowerCase().includes(lowerSearch))
      );
    }

    // 2. Apply Filters
    switch (filterType) {
      case "inStock":
        data = data.filter((row) => Number(row.qteDispo) > 0);
        break;
      case "outStock":
        data = data.filter((row) => Number(row.qteDispo) === 0);
        break;
      case "negativeStock":
        data = data.filter((row) => Number(row.qteDispo) < 0);
        break;
      case "lowStock":
        data = data.filter((row) => Number(row.qteDispo) > 0 && Number(row.qteDispo) < 5);
        break;
      case "negativeMargin":
        data = data.filter((row) => Number(row.margin) < 0);
        break;
      default:
        break;
    }

    return data;
  }, [stockData, searchTerm, filterType]);

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handlePageChange = (direction) => {
    setPage((prev) => {
      const next = prev + direction;
      if (next < 0) return 0;
      if (next >= totalPages) return totalPages - 1;
      return next;
    });
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleFilterClick = (type) => {
    setFilterType(type);
    setPage(0);
  }

  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, row) => {
        const qty = Number(row.qteDispo) || 0;
        const price = Number(row.price) || 0;
        const buyingPrice = Number(row.buyingPrice) || 0;
        const margin = Number(row.margin) || 0;

        // Only count positive stock for inventory value (Asset Value)
        // Or should it be net? Standard inventory valuation is for items on hand (qty > 0).
        // If the user wants "Net Position", we include negatives.
        // Given the request "Stock Management", seeing negative value helps identify issues.
        // We'll keep it simple: straight multiplication. 
        
        acc.totalValue += price * qty;
        acc.totalMargin += margin * qty;
        acc.totalBuying += buyingPrice * qty;
        acc.totalQty += qty;
        return acc;
      },
      { totalValue: 0, totalMargin: 0, totalBuying: 0, totalQty: 0 }
    );
  }, [filteredData]);

  const globalMarginPercent = totals.totalValue > 0 
    ? (totals.totalMargin / totals.totalValue) * 100 
    : 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(amount);
  };

  return (
    <div className="stock-page">
      <div className="stock-header">
        <div>
          <h1>Stock Management</h1>
          <p className="muted">Manage your product inventory and margins.</p>
        </div>
        <input 
            type="text" 
            placeholder="Search products..." 
            className="search-input"
            value={searchTerm}
            onChange={handleSearchChange}
        />
      </div>

      <div className="filters-row">
        <button 
          className={`filter-chip ${filterType === 'all' ? 'active' : ''}`}
          onClick={() => handleFilterClick('all')}
        >
          All Products
        </button>
        <button 
          className={`filter-chip ${filterType === 'inStock' ? 'active' : ''}`}
          onClick={() => handleFilterClick('inStock')}
        >
          In Stock ({'>'} 0)
        </button>
        <button 
          className={`filter-chip ${filterType === 'outStock' ? 'active' : ''}`}
          onClick={() => handleFilterClick('outStock')}
        >
          Stock Empty (= 0)
        </button>
        <button 
          className={`filter-chip ${filterType === 'negativeStock' ? 'active' : ''}`}
          onClick={() => handleFilterClick('negativeStock')}
        >
          Negative Stock ({'<'} 0)
        </button>
        <button 
          className={`filter-chip ${filterType === 'lowStock' ? 'active' : ''}`}
          onClick={() => handleFilterClick('lowStock')}
        >
          Low Stock ({'<'} 5)
        </button>
        <button 
          className={`filter-chip ${filterType === 'negativeMargin' ? 'active' : ''}`}
          onClick={() => handleFilterClick('negativeMargin')}
        >
          Negative Margin
        </button>
      </div>

      <div className="table-card">
        <div className="table-header">
          <div>
            <h2>Inventory List</h2>
            <p className="muted">
              {filteredData.length} items found. Showing page {page + 1} of {totalPages || 1}.
            </p>
          </div>
          <div className="pagination-controls">
            <label>
              Rows per page
              <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                {[20, 50, 100, 200].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
            <div className="pager">
              <button onClick={() => handlePageChange(-1)} disabled={page === 0}>
                Prev
              </button>
              <span>
                {page + 1} / {totalPages || 1}
              </span>
              <button
                onClick={() => handlePageChange(1)}
                disabled={page + 1 >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="empty-state">Loading stock data...</div>
        ) : filteredData.length === 0 ? (
          <div className="empty-state">No products found matching criteria.</div>
        ) : (
          <div className="table-wrapper">
            <table className="stock-table">
              <thead>
                <tr>
                  <th>Fournisseur</th>
                  <th>Réf produit</th>
                  <th>Déclinaison</th>
                  <th>Prix Achat</th>
                  <th>Prix Vente</th>
                  <th>Marge</th>
                  <th>% Marge</th>
                  <th>Qte Dispo</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row) => (
                  <tr key={row.id}>
                    <td>{row.supplier}</td>
                    <td>{row.ref}</td>
                    <td className="cell-ellipsis" title={row.declinaison}>{row.declinaison}</td>
                    <td>{formatCurrency(row.buyingPrice)}</td>
                    <td>{formatCurrency(row.price)}</td>
                    <td>{formatCurrency(row.margin)}</td>
                    <td>{row.percentMargin.toFixed(2)} %</td>
                    <td>{row.qteDispo}</td>
                  </tr>
                ))}
                <tr className="totals-row">
                  <td colSpan="3">Total</td>
                  <td>{formatCurrency(totals.totalBuying)}</td>
                  <td>{formatCurrency(totals.totalValue)}</td>
                  <td>{formatCurrency(totals.totalMargin)}</td>
                  <td>{globalMarginPercent.toFixed(2)} %</td>
                  <td>{totals.totalQty}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
